import type { ServiceCategoryRow } from '@/api/booking/getServiceCategoriesQuery';
import type { Service } from '@/types/booking';
import { hasBarberAssignment } from '@/utils/booking/hasBarberAssignment';
import { isCompatibleWithCart } from '@/utils/buildDisplayServices';

interface GetSubcategoryCountsParams {
  l1Id: string;
  rows: ServiceCategoryRow[];
  services: Service[];
  cartServices: Service[];
  /**
   * Skips services with no `'barber'` assignment, as `buildDisplayServices`
   * does for "Any professional". Cart services are exempt so they always count
   * for their own L2s.
   */
  requireBarberAssignment: boolean;
}

/**
 * Counts, per visible L2 under `l1Id`, the services that one professional
 * offers together with every cart service (`isCompatibleWithCart`). A service
 * in several L2s counts once in each.
 */
export function getSubcategoryCounts({
  l1Id,
  rows,
  services,
  cartServices,
  requireBarberAssignment,
}: GetSubcategoryCountsParams): Map<string, number> {
  const l2RowById = new Map(
    rows
      .filter((row) => row.parentCategoryId === l1Id && row.visible !== false)
      .map((row) => [row.id, row] as const),
  );
  const cartServiceIds = new Set(cartServices.map((service) => service.id));

  const counts = new Map<string, number>();
  for (const service of services) {
    const isInCart = cartServiceIds.has(service.id);
    if (requireBarberAssignment && !isInCart && !hasBarberAssignment(service)) {
      continue;
    }
    if (!isCompatibleWithCart(service, cartServices)) continue;

    for (const category of service.categories ?? []) {
      if (category.visible === false || !l2RowById.has(category.id)) continue;
      counts.set(category.id, (counts.get(category.id) ?? 0) + 1);
    }
  }
  return counts;
}
